import { FormattedMessage } from 'react-intl';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';

import { messages } from '../../messages';
import { ValidationErrorType } from '../../types';
import { Banner } from '../banner';

import type { HtmlValidationError } from '../../types';
import type { ReactNode } from 'react';

type ValidationErrorBannerProps = {
  error: HtmlValidationError;
};

const boldChunks = (chunks: ReactNode) => <strong>{chunks}</strong>;

const ErrorMessage = ({ error }: ValidationErrorBannerProps) => {
  switch (error.type) {
    case ValidationErrorType.UnsupportedElement:
    case ValidationErrorType.UnsupportedAttribute:
      return (
        <FormattedMessage
          {...messages.printUnsupported}
          values={{
            lineNumber: error.line,
            detail: error.detail,
            b: boldChunks,
          }}
        />
      );
    case ValidationErrorType.Syntax:
      return (
        <FormattedMessage
          {...messages.syntaxError}
          values={{
            lineNumber: error.line,
            b: boldChunks,
          }}
        />
      );
    case ValidationErrorType.MalformedLiquid:
      return (
        <FormattedMessage
          {...messages.malformedLiquidError}
          values={{
            lineNumber: error.line,
            detail: error.detail,
            b: boldChunks,
          }}
        />
      );
    case ValidationErrorType.InvalidField:
      return (
        <FormattedMessage
          {...messages.invalidField}
          values={{
            lineNumber: error.line,
            detail: error.detail,
            b: boldChunks,
          }}
        />
      );
    case ValidationErrorType.ImageFileSizeTooLarge:
      return (
        <FormattedMessage
          {...messages.imageValidationError}
          values={{
            lineNumber: error.line,
            detail: <FormattedMessage {...messages.imageFileSizeTooLarge} values={{ detail: error.detail }} />,
            b: boldChunks,
          }}
        />
      );
    case ValidationErrorType.ImageUnsupportedType:
    case ValidationErrorType.ImageInvalidUrl:
    case ValidationErrorType.ImageBlockedHost:
      return (
        <FormattedMessage
          {...messages.imageValidationError}
          values={{
            lineNumber: error.line,
            detail: <FormattedMessage {...messages.imageIsNotAllowed} values={{ detail: error.detail }} />,
            b: boldChunks,
          }}
        />
      );
    default:
      return null;
  }
};

export const ValidationErrorBanner = ({ error }: ValidationErrorBannerProps) => (
  <Banner
    content={<Text textType={TextType.Caption} value={<ErrorMessage error={error} />} />}
    layout={BannerLayout.Inline}
    status={BannerStatus.Error}
  />
);
